import {storeToRefs} from 'pinia';
import {compact} from 'lodash-es';
import type {MenuLink} from '~/types/header';
import {useStoryblokHypermarketsStore} from '~/stores/storyblokHypermarkets';
import {GLOBAL_BRANCH_SLUG, GLOBUS_ONLINE_GSOA_IDS} from '#shared/constants';
import HeaderMegaMenu from '~/components/header/MegaMenu.vue';

export function useHeaderMenu(isDesktop: Ref<boolean>) {
	const hypermarketsStore = useStoryblokHypermarketsStore();
	const {selectedHypermarket, selectedHypermarketInstance} = storeToRefs(hypermarketsStore);

	const branchSpecificPath = useBranchSpecificPath();
	const activeSlug = computed(() => selectedHypermarket.value ? selectedHypermarket.value : GLOBAL_BRANCH_SLUG);
	const {data: branchPageAvailability} = useBranchPageAvailability(activeSlug);

	const visibleGlobusOnline = computed(() =>
		GLOBUS_ONLINE_GSOA_IDS.includes(selectedHypermarketInstance.value?.content.gsoaId ?? ''),
	);

	const menuItemsSecondary = computed<MenuLink[]>(() => compact([
		{
			label: 'Naše služby a produkty',
			url: '/globus/sluzby',
			items: [
				{
					label: 'Restaurace Globus',
					url: '/globus/sluzby/restaurace',
				},
				{
					label: 'Řeznictví Globus',
					url: '/globus/sluzby/reznictvi',
				},
				{
					label: 'Pekárna Globus',
					url: '/globus/sluzby/pekarna',
				},
				{
					label: 'Café Globus',
					url: '/globus/sluzby/cafe-globus',
				},
				{
					label: 'Čerpací stanice a myčka',
					url: '/globus/sluzby/cerpaci-stanice-a-mycka',
				},
				{
					label: 'Klenoty',
					url: '/globus/sluzby/klenoty',
				},
				{
					label: 'Dárkové karty a koše',
					url: '/globus/sluzby/darkovy-kos',
				},
				{
					label: 'Mobilní aplikace',
					url: '/globus/sluzby/mobilni-aplikace-muj-globus',
				},
				{
					label: 'Další...',
					url: '/globus/sluzby',
				},
			],
		},
		{
			label: 'Blog',
			url: '/blog',
		},
		{
			label: 'Recepty',
			url: '/recepty',
		},
		{
			label: 'O nás',
			url: '/o-nas',
			items: [
				{
					label: 'Hypermarket a sídlo společnosti ',
					url: '/o-nas/sidlo-spolecnosti',
				},
				{
					label: 'Malé prodejny Globus Fresh',
					url: '/o-nas/sidlo-spolecnosti#fresh-globus',
					disableActiveState: true,
				},
				{
					label: 'Globus Bistro',
					url: '/o-nas/sidlo-spolecnosti#bistra-globus',
					disableActiveState: true,
				},
				{
					label: 'Tiskové zprávy',
					url: '/o-nas/tiskove-zpravy',
				},
				{
					label: 'Napište nám',
					url: '/napiste-nam',
				},
				{
					label: 'Další...',
					url: '/o-nas',
				},
			],
		},
		{
			label: 'Věrnostní program',
			url: '/vernostni-program',
		},
		{
			label: 'Kontakty',
			url: '/o-nas/sidlo-spolecnosti',
		},
	]));

	const menuItemHypermarket = computed<MenuLink>(() => {
		return {
			label: selectedHypermarketInstance.value?.content?.name ? selectedHypermarketInstance.value.content.name : 'Vybrat hypermarket',
			dropdown: HeaderMegaMenu,
		};
	});

	const hypermarketMenuItem = computed<MenuLink>(() => ({
		label: selectedHypermarketInstance.value?.branchType === 'fresh'
			? 'Prodejna'
			: selectedHypermarket.value
				? 'Hypermarket'
				: 'Hypermarkety',
		url: selectedHypermarket.value ? `/${selectedHypermarket.value}` : '/o-nas/sidlo-spolecnosti',
	}));

	const menuItems = computed<MenuLink[]>(() => {
		const primaryItems = compact([
			(!selectedHypermarket.value || branchPageAvailability.value.hasLeaflets) && {
				label: selectedHypermarket.value ? 'Leták' : 'Letáky',
				url: branchSpecificPath('/letaky').value,
			},
			{
				label: 'Akční nabídka',
				url: branchSpecificPath('/hypermarket/akcni-nabidka').value,
			},
			{
				label: 'Celá nabídka',
				url: branchSpecificPath('/hypermarket/cela-nabidka').value,
			},
		]);

		const serviceItems = [
			selectedHypermarket.value && branchPageAvailability.value.hasRestaurant && {
				label: 'Restaurace',
				url: branchSpecificPath('/sluzby/restaurace').value,
			},
			selectedHypermarket.value && branchPageAvailability.value.hasServices && {
				label: 'Služby',
				url: branchSpecificPath('/sluzby').value,
			},
			branchPageAvailability.value.hasOnlineOrder && {
				label: 'Objednávky a rezervace',
				url: branchSpecificPath('/online-objednavka').value,
			},
			visibleGlobusOnline.value && {
				label: 'Globus Online',
				url: `https://globusonline.cz/?defaultStore=${selectedHypermarketInstance.value?.slug}`,
				target: '_blank',
				suffixIcon: 'custom/external-link',
			},
		];

		if (!isDesktop.value && selectedHypermarket.value) {
			return compact([
				hypermarketMenuItem.value,
				...primaryItems,
				...serviceItems,
			]);
		}

		return compact([
			...primaryItems,
			hypermarketMenuItem.value,
			...serviceItems,
		]);
	});

	return {
		menuItemsSecondary,
		menuItems,
		menuItemHypermarket,
	};
}
